import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

export default class HeaderHandler {
	init() {
		function headerHeight() {
			const header = document.querySelector('header');
			if (header) {
				const height = header.offsetHeight;
				document.documentElement.style.setProperty('--header', `${height}px`);
			}
		}
		headerHeight();
		window.addEventListener("resize", () => {
			headerHeight();
		});

		this.initMobileMenu();
		this.updateActiveLinks();
		this.initDesktopNav();
	}
	
	scroll() {
		const header = document.querySelector('header');
		if (!header) return;
		
		const firstChild = header.firstElementChild;
		if (!firstChild) return;
		
		const updateScrollTrigger = () => {
			ScrollTrigger.getAll().forEach(trigger => {
				if (trigger.vars.id === 'headerScroll') {
					trigger.kill();
				}
			});

			if (window.innerWidth >= 768) {
				const firstChildHeight = firstChild.offsetHeight;

				ScrollTrigger.create({
					id: 'headerScroll',
					trigger: header,
					start: `${header.offsetHeight} top`,
					onEnter: () => {
						header.style.transform = `translateY(-${firstChildHeight}px)`;
					},
					onLeaveBack: () => {
						header.style.transform = "translateY(0)";
					}
				});
			} else {
				header.style.transform = '';
			}
		};
		updateScrollTrigger();
		window.addEventListener('resize', updateScrollTrigger);
	}
	
	reset() {
		const header = document.querySelector('header');
		if (header) {
			header.style.transform = '';
		}
	}

	initMobileMenu() {
		const hamburgerBtn = document.querySelector('[data-mobile-toggle]');
		const mobileMenu = document.querySelector('[data-mobile-menu]');
		const mobileOverlay = document.querySelector('[data-mobile-overlay]');
		const mobileCloseBtn = document.querySelector('[data-mobile-close]');
		const submenuToggles = document.querySelectorAll('[data-submenu-toggle]');
		const menuLinks = document.querySelectorAll('[data-mobile-menu] a');

		if (!hamburgerBtn || !mobileMenu || !mobileOverlay) return;

		// Function to close all submenus
		const closeAllSubmenus = (immediate = false) => {
			const allSubmenus = document.querySelectorAll('[data-mobile-submenu]');
			const allToggles = document.querySelectorAll('[data-submenu-toggle]');
			const isMobile = window.innerWidth < 768;
			
			if (immediate) {
				// Immediate close without animation
				allSubmenus.forEach(submenu => {
					submenu.classList.add('max-sm:translate-x-full');
					if (!isMobile) {
						submenu.style.maxHeight = '0px';
					} else {
						submenu.style.removeProperty('max-height');
					}
				});
			} else if (isMobile) {
				// For mobile, close submenus first, then main menu
				const openSubmenus = document.querySelectorAll('[data-mobile-submenu]:not(.max-sm\\:translate-x-full)');
				let hasOpenSubmenus = openSubmenus.length > 0;
				
				openSubmenus.forEach((submenu, index) => {
					setTimeout(() => {
						submenu.classList.add('max-sm:translate-x-full');
					}, index * 50); // Stagger the closing
				});
				
				// Return whether we had open submenus
				return hasOpenSubmenus;
			} else {
				// Desktop behavior
				allSubmenus.forEach(submenu => {
					submenu.style.maxHeight = '0px';
				});
			}
			
			allToggles.forEach(toggle => {
				toggle.setAttribute('aria-expanded', 'false');
			});
			
			return false;
		};

		// Toggle main mobile menu
		hamburgerBtn.addEventListener('click', () => {
			const isExpanded = hamburgerBtn.getAttribute('aria-expanded') === 'true';
			const isMobile = window.innerWidth < 768;
			
			if (isExpanded) {
				// Closing menu
				if (isMobile) {
					// Check if submenus are open
					const hasOpenSubmenus = closeAllSubmenus();
					
					// Delay main menu close if submenus were open
					setTimeout(() => {
						hamburgerBtn.setAttribute('aria-expanded', 'false');
						mobileMenu.classList.add('max-sm:translate-x-full');
						mobileOverlay.classList.add('!opacity-0', 'pointer-events-none');
						document.body.classList.remove('overflow-hidden');
					}, hasOpenSubmenus ? 200 : 0);
				} else {
					// Desktop - immediate close
					hamburgerBtn.setAttribute('aria-expanded', 'false');
					mobileMenu.classList.add('max-sm:translate-x-full');
					mobileOverlay.classList.add('!opacity-0', 'pointer-events-none');
					document.body.classList.remove('overflow-hidden');
					closeAllSubmenus();
				}
			} else {
				// Opening menu
				hamburgerBtn.setAttribute('aria-expanded', 'true');
				mobileMenu.classList.remove('max-sm:translate-x-full');
				mobileOverlay.classList.remove('!opacity-0', 'pointer-events-none');
				document.body.classList.add('overflow-hidden');
				
				// Ensure all submenus are properly closed when opening menu
				if (isMobile) {
					closeAllSubmenus(true); // Immediate close without animation
				}
			}
		});

		// Close menu when clicking overlay
		mobileOverlay.addEventListener('click', () => {
			hamburgerBtn.click();
		});

		// Close menu when clicking close button
		if (mobileCloseBtn) {
			mobileCloseBtn.addEventListener('click', () => {
				hamburgerBtn.click();
			});
		}

		// Handle submenu toggles
		submenuToggles.forEach(btn => {
			btn.addEventListener('click', () => {
				const targetId = btn.getAttribute('data-submenu-toggle');
				const submenu = document.querySelector(`[data-mobile-submenu="${targetId}"]`);
				const isExpanded = btn.getAttribute('aria-expanded') === 'true';
				const isMobile = window.innerWidth < 768;

				if (submenu) {
					btn.setAttribute('aria-expanded', !isExpanded);
					
					if (isMobile) {
						// Mobile behavior only
						if (isExpanded) {
							submenu.classList.add('max-sm:translate-x-full');
						} else {
							submenu.classList.remove('max-sm:translate-x-full');
						}
					} else {
						// Desktop behavior only
						if (isExpanded) {
							submenu.style.maxHeight = '0px';
						} else {
							submenu.style.maxHeight = submenu.scrollHeight + 'px';
						}
					}
				}
			});
		});

		// Handle submenu back buttons
		const submenuBackBtns = document.querySelectorAll('[data-submenu-back]');
		submenuBackBtns.forEach(btn => {
			btn.addEventListener('click', () => {
				const targetId = btn.getAttribute('data-submenu-back');
				const submenu = document.querySelector(`[data-mobile-submenu="${targetId}"]`);
				const toggle = document.querySelector(`[data-submenu-toggle="${targetId}"]`);
				
				if (submenu && toggle) {
					// Close submenu
					submenu.classList.add('max-sm:translate-x-full');
					toggle.setAttribute('aria-expanded', 'false');
				}
			});
		});

		// Handle submenu close buttons
		const submenuCloseBtns = document.querySelectorAll('[data-mobile-submenu-close]');
		submenuCloseBtns.forEach(btn => {
			btn.addEventListener('click', () => {
				// Close the entire menu
				hamburgerBtn.click();
			});
		});

		// Close menu when clicking on a link
		menuLinks.forEach(link => {
			link.addEventListener('click', (e) => {
				// Check if this is an internal link (not external)
				const href = link.getAttribute('href');
				const isInternalLink = href && (href.startsWith('/') || href.startsWith(window.location.origin));
				const isMobile = window.innerWidth < 768;
				
				if (isInternalLink && isMobile) {
					// For mobile, trigger the hamburger click which handles sequencing
					const isExpanded = hamburgerBtn.getAttribute('aria-expanded') === 'true';
					if (isExpanded) {
						hamburgerBtn.click();
					}
				}
			});
		});
	}

	updateActiveLinks() {
		const header = document.querySelector('header');
		if (!header) return;

		// Get all navigation links (both desktop and mobile)
		const navLinks = header.querySelectorAll('nav a');
		const navArrows = header.querySelectorAll('nav span svg');
		const currentUrl = location.href;

		// Remove active styles from all links and arrows
		navLinks.forEach(link => {
			link.classList.remove('text-contrast-2', '!text-contrast-2', 'text-primary-3', '!text-primary-3');
		});
		navArrows.forEach(arrow => {
			arrow.classList.remove('fill-contrast-2', '!fill-contrast-2');
		});

		// Find and style active links
		navLinks.forEach(link => {
			const linkUrl = link.href;

			// Check for exact match or if current URL starts with link URL (for parent pages)
			if (linkUrl === currentUrl ||
				(linkUrl !== location.origin + '/' && currentUrl.startsWith(linkUrl) && linkUrl.endsWith('/'))) {

				// Check if this link is in secondary navigation
				const isSecondaryNav = link.closest('nav[role="secondary-menubar"]');

				if (isSecondaryNav) {
					// Add primary active color class to link
					link.classList.add('!text-primary-3');
				} else {
					// Add standard active color class to link
					link.classList.add('!text-contrast-2');

					// Find and style associated arrow (next sibling span with SVG)
					const parentLi = link.closest('li');
					if (parentLi) {
						const arrowSpan = parentLi.querySelector('span svg');
						if (arrowSpan) {
							arrowSpan.classList.add('!fill-contrast-2');
						}
					}
				}
			}
		});
	}

	initDesktopNav() {
		// Only run on desktop
		if (window.innerWidth < 640) return; // sm breakpoint

		const childItems = document.querySelectorAll('[data-child-id]');

		childItems.forEach(child => {
			const childId = child.getAttribute('data-child-id');
			const parentDropdown = child.closest('[data-mobile-submenu]');
			const grandchildrenContainer = parentDropdown ? parentDropdown.querySelector(`[data-grandchildren-for="${childId}"]`) : null;

			child.addEventListener('mouseenter', () => {
				if (parentDropdown) {
					// Find all grandchildren containers in this dropdown
					const allContainers = parentDropdown.querySelectorAll('[data-grandchildren-for]');

					// Hide all grandchildren containers
					allContainers.forEach(container => {
						container.classList.add('hidden');
					});

					// Show this child's grandchildren if they exist
					if (grandchildrenContainer) {
						grandchildrenContainer.classList.remove('hidden');
					}

					// Update opacity states for all child items
					const allChildItems = parentDropdown.querySelectorAll('[data-child-id]');
					allChildItems.forEach(item => {
						const excerpt = item.querySelector('p.text-1-40');
						if (item === child) {
							item.classList.add('!opacity-100');
							item.classList.remove('opacity-30');
							// Change excerpt color to text-primary-2
							if (excerpt) {
								excerpt.classList.remove('text-1-40');
								excerpt.classList.add('text-primary-2');
							}
						} else {
							item.classList.add('opacity-30');
							item.classList.remove('!opacity-100');
							// Keep excerpt as text-1-40
							if (excerpt) {
								excerpt.classList.add('text-1-40');
								excerpt.classList.remove('text-primary-2');
							}
						}
					});
				}
			});
		});

		// Keep grandchildren visible when hovering over the dropdown
		const dropdowns = document.querySelectorAll('.group > div[data-mobile-submenu]');
		dropdowns.forEach(dropdown => {
			// Activate first child when entering dropdown (only if menu has grandchildren)
			dropdown.parentElement.addEventListener('mouseenter', () => {
				const hasGrandchildren = dropdown.getAttribute('data-has-grandchildren') === 'true';

				// Only activate first child if this menu has grandchildren
				if (hasGrandchildren) {
					const firstChild = dropdown.querySelector('[data-child-id]');
					if (firstChild) {
						const childId = firstChild.getAttribute('data-child-id');
						const grandchildrenContainer = dropdown.querySelector(`[data-grandchildren-for="${childId}"]`);
						const allContainers = dropdown.querySelectorAll('[data-grandchildren-for]');

						// Hide all grandchildren containers first
						allContainers.forEach(container => {
							container.classList.add('hidden');
						});

						// Show first child's grandchildren if they exist
						if (grandchildrenContainer) {
							grandchildrenContainer.classList.remove('hidden');
						}

						// Set first child as active, others dimmed
						const allChildItems = dropdown.querySelectorAll('[data-child-id]');
						allChildItems.forEach(item => {
							const excerpt = item.querySelector('p');
							if (item === firstChild) {
								item.classList.add('!opacity-100');
								item.classList.remove('opacity-30');
								if (excerpt) {
									excerpt.classList.remove('text-1-40');
									excerpt.classList.add('text-primary-2');
								}
							} else {
								item.classList.add('opacity-30');
								item.classList.remove('!opacity-100');
								if (excerpt) {
									excerpt.classList.add('text-1-40');
									excerpt.classList.remove('text-primary-2');
								}
							}
						});
					}
				}
			});

			dropdown.addEventListener('mouseleave', () => {
				// Hide all grandchildren when leaving the dropdown
				setTimeout(() => {
					dropdown.querySelectorAll('[data-grandchildren-for]').forEach(container => {
						container.classList.add('hidden');
					});

					// Reset opacity states and excerpt colors
					dropdown.querySelectorAll('[data-child-id]').forEach(item => {
						item.classList.remove('opacity-30', '!opacity-100');
						const excerpt = item.querySelector('p.text-primary-2');
						if (excerpt) {
							excerpt.classList.add('text-1-40');
							excerpt.classList.remove('text-primary-2');
						}
					});
				}, 100);
			});
		});
	}
}